/* The recording bar (APP-104 §3.2): docked at the bottom of the window, navy with white text,
   44 px controls. Tokens only. Reduced motion (the OS or My settings) makes the dot solid and the
   level bars still: the component sets data-still, and the bars aren't moved in script. */

.bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 65;
  padding: 10px var(--content-gutter) calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 3px solid var(--brand-cyan);
  background: var(--brand-navy);
  box-shadow: 0 -12px 32px var(--navy-a35);
  color: var(--text-on-dark);
  outline: none;
}

.bar:focus-visible {
  box-shadow:
    0 -12px 32px var(--navy-a35),
    inset 0 0 0 3px var(--focus-ring-color);
}

.inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: var(--content-max);
  margin: 0 auto;
}

/* One line of the words as they come: once it's full, the newest stay in view (the component
   scrolls it to the end) and the oldest fade out at the left. */
.words {
  overflow: hidden;
  margin: 0;
  color: var(--text-on-dark-soft);
  font-size: 15px;
  line-height: 1.5;
  white-space: nowrap;
}

.words[data-overflowing='true'] {
  mask-image: linear-gradient(90deg, transparent 0, var(--white) 48px);
}

.words:empty {
  display: none;
}

.partial {
  color: var(--text-on-dark-muted);
}

.row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 8px 24px;
  align-items: center;
}

.state {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: var(--control-height);
}

.status {
  margin: 0;
  font-size: 16px;
  font-weight: var(--fw-semibold);
  white-space: nowrap;
}

/* The red dot: decorative; the word "Recording" beside it carries the state. */
.dot {
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--danger);
  box-shadow: 0 0 0 2px var(--white-a22);
  animation: pulse 1.4s ease-in-out infinite;
}

@keyframes pulse {
  50% {
    opacity: 0.35;
  }
}

.spinner {
  flex: none;
  width: 16px;
  height: 16px;
  border: 2px solid var(--white-a30);
  border-top-color: var(--white);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.dot[data-still='true'],
.spinner[data-still='true'] {
  animation: none;
}

.timer {
  color: var(--text-on-dark-soft);
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.levels {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 22px;
}

.levelBar {
  width: 3px;
  height: 22px;
  border-radius: 2px;
  background: var(--brand-cyan);
  transform-origin: center;
}

.hint {
  min-width: 0;
}

.say {
  margin: 0;
  font-size: 15px;
  font-weight: var(--fw-medium);
}

.note {
  margin: 2px 0 0;
  color: var(--text-on-dark-muted);
  font-size: 13px;
  line-height: 1.4;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.cancel {
  display: inline-grid;
  place-items: center;
  width: var(--control-height);
  height: var(--control-height);
  padding: 0;
  border: 1px solid var(--white-a30);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--white);
  cursor: pointer;
}

.cancel[data-hovered] {
  background: var(--white-a10);
}

.cancel[data-focus-visible] {
  outline: none;
  box-shadow: var(--focus-halo);
}

@media (max-width: 899px) {
  .bar {
    padding-right: 16px;
    padding-left: 16px;
  }

  .row {
    grid-template-columns: minmax(0, 1fr);
    gap: 6px;
  }

  .actions {
    justify-content: flex-start;
  }
}

/* --- In the review: MicHelp and the notices ------------------------------------------------- */

.micHelp {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 6px 12px;
  align-items: start;
  padding: 12px 14px;
  border: 1px solid var(--border-urgent);
  border-left: 3px solid var(--text-gold);
  border-radius: var(--radius-sm);
  background: var(--surface-urgent);
  color: var(--text-body);
}

.micIcon {
  margin-top: 2px;
  color: var(--text-gold);
}

.micTitle {
  margin: 0;
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-semibold);
}

.micStep {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.5;
}

.micActions {
  grid-column: 2;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.notice {
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid var(--status-info-ink);
  border-radius: var(--radius-sm);
  background: var(--status-info-bg);
  color: var(--text-body);
  font-size: 14px;
  line-height: 1.5;
}

.record {
  margin-top: 8px;
}
